import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import MUIDataTable from 'mui-datatables';

import { withTranslation } from 'react-i18next';

import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { trackEvent } from '@intercom/messenger-js-sdk';

import DevicesIcon from '@/shared/assets/icons/sidenav/devices.svg?react';
import OrganizationsIcon from '@/shared/assets/icons/sidenav/organizations.svg?react';
import AddClientIcon from '@/shared/assets/icons/sidenav/practitioners.svg?react';
import RemoteViewIcon from '@/shared/assets/icons/sidenav/remote-view.svg?react';
import TreatmentIcon from '@/shared/assets/icons/sidenav/treatment.svg?react';

import useAuthenticatedUser from '../hooks/useAuthenticatedUser';

import { showToast } from '../utils/toast';
import getMuiTheme from '../utils/MUI-Style';
import tableConfig from '../utils/MUI-table-config';
import { classNames, getResponsiveDimension } from '../utils';
import { muiTableLoadingSkeleton } from '../utils/MUI-table-loading-skaleton';

import { sessionService } from '../services/sessions';
import { dashboardActions } from '../store/actions/dashboard.actions';
import { sessionActions } from '../store/actions/session.actions';

// eslint-disable-next-line import/no-named-as-default-member
import LineGraph from '../components/LineGraph';
// eslint-disable-next-line import/no-named-as-default-member
import LineGraphReverse from '../components/LineGraphReverse';
import { CustomMuiDataTableComplete } from '../components/UI/MuiDatatable/CustomMuiDataTableComplete/CustomMuiDataTableComplete';
import { dashboardStyle } from './style';

const DashboardPage = function ({
  history,
  authentication: {
    userIsAdmin,
    practitioner,
    canManageTeams,
    teams,
    foundUser,
  },
  i18n,
  devices: { devices, isLoading: deviceLoading },
}) {
  useAuthenticatedUser();
  const isDesktop = useMediaQuery('(min-width: 1025px)');
  const isLaptop = useMediaQuery('(max-width: 1024px) and (min-width: 768px)');
  const { dashboard } = dashboardStyle;
  const [sessions, setSessions] = useState([]);
  const [sessionModalIsOpen, setSessionModalIsOpen] = useState(false);
  const { roleName } = foundUser || {};
  const forbiddenSessionRoles = ['device-admin'];
  const allowedToSeeRecentSessions = !forbiddenSessionRoles.includes(roleName);

  const tableRef = useRef();

  const isLoading = deviceLoading || sessions.length <= 0;

  /**
   * Sets table graph height
   */
  const graphHeight = useMemo(() => {
    if (isDesktop) return getResponsiveDimension(38);
    else if (isLaptop) return getResponsiveDimension(37, 'laptop');
    else return getResponsiveDimension(50, 'mobile');
  }, [isDesktop, isLaptop]);

  /**
   * Sets table graph width
   */
  const graphWidth = useMemo(() => {
    if (isDesktop) return getResponsiveDimension(150);
    else if (isLaptop) return getResponsiveDimension(130, 'laptop');
    else return getResponsiveDimension(250, 'mobile');
  }, [isDesktop, isLaptop]);

  const options = tableConfig({
    pagination: false,
    search: false,
    filter: false,
    sort: false,
    download: false,
    print: false,
    viewColumns: false,
    tableBodyMaxHeight: '100%',

    onRowClick: (rowData) => {
      const id = rowData[0]?.props?.children;
      history.push({
        pathname: `/admin/sessions/${id}`,
        state: {},
      });
    },
  });

  useEffect(() => {
    const fetchRecentSessions = async () => {
      if (!allowedToSeeRecentSessions) return;
      try {
        const result = await sessionService.getAllSessions(
          5,
          0,
          'start_date DESC',
        );
        setSessions(result?.data?.sessions);
      } catch (error) {
        showToast(i18n.t('errors.get.sessions'), 'error', 3000);
      }
    };

    fetchRecentSessions();
    trackEvent('page-visit', {
      name: 'Home',
    });
  }, []);

  const handleModalOpen = () => {
    setSessionModalIsOpen(true);
  };

  const handleCloseModal = () => {
    setSessionModalIsOpen(false);
  };

  const AdminButtons = useMemo(() => {
    if (!userIsAdmin) {
      return undefined;
    }
    return (
      <>
        <NavLink
          className='btn-quick-access btn-icon w-full '
          to='/admin/devices?create=true'
        >
          {i18n.t('buttons.create.device')}
          <DevicesIcon className='nav-option-icon devices' />
        </NavLink>
        <NavLink
          className='btn-quick-access btn-icon w-full '
          to='/admin/organizations?create=true'
        >
          {i18n.t('buttons.create.organization')}
          <OrganizationsIcon className='nav-option-icon organizations' />
        </NavLink>
      </>
    );
  }, []);

  const sessionsToShow = sessions.map((item) =>
    sessionActions.createSessionObject(item),
  );
  const listOfKeysToHide = [
    i18n.t('forms.gen_id'),
    'id',
    i18n.t('forms.delta'),
  ];
  const excludedkeysToDownload = [i18n.t('forms.notes')];
  let attributes = [];
  if (sessionsToShow.length > 0) {
    attributes = [...new Set(Object.keys(sessionsToShow[0]))];
    attributes = attributes.map((key) => {
      if (listOfKeysToHide.includes(key)) {
        return {
          name: key,
          options: {
            display: false,
          },
        };
      }
      if (excludedkeysToDownload.includes(key))
        return {
          name: key,
          options: {
            display: false,
            sort: false,
            download: true,
            print: true,
            viewColumns: false,
          },
        };
      if (key === i18n.t('forms.sud_scores')) {
        return {
          name: key,
          options: {
            display: true,
            filter: false,
            customBodyRender: (value) =>
              value.length > 0 ? (
                <LineGraph
                  small
                  values={value}
                  height={graphHeight}
                  width={graphWidth}
                />
              ) : (
                '-'
              ),
          },
        };
      }
      if (key === i18n.t('forms.validity_of_cognition_array')) {
        return {
          name: key,
          options: {
            display: true,
            filter: false,
            customBodyRender: (value) =>
              value.length > 0 ? (
                <LineGraphReverse
                  small
                  values={value}
                  height={graphHeight}
                  width={graphWidth}
                />
              ) : (
                '-'
              ),
          },
        };
      }
      if (key === i18n.t('forms.completed')) {
        return {
          name: key,
          options: {
            display: true,
            filter: false,
            customBodyRender: (value, tableMeta) => (
              <CustomMuiDataTableComplete
                result={tableMeta.rowData[8]}
                className='dashboard'
              />
            ),
          },
        };
      }
      return key;
    });
  }

  /**
   * Checks does table have data
   */
  const isDataTableEmpty = sessionsToShow.length <= 0;

  return (
    <>
      <Box className='organizations-wrapper' sx={dashboard}>
        <Box
          className='content-wrapper flex items-center justify-center m-auto'
          style={{ minHeight: '50%' }}
        >
          <Box
            className='quick-start row h-full mr-2'
            style={{ minWidth: '200px' }}
          >
            <Typography variant='h5' className='quick-access-title'>
              {i18n.t('quick-access')}
            </Typography>
            {practitioner ? (
              <>
                <NavLink
                  className='btn-quick-access btn-icon w-full'
                  to='/admin/treatments/create'
                >
                  {i18n.t('buttons.create.treatment')}
                  <TreatmentIcon className='nav-option-icon treatment' />
                </NavLink>
              </>
            ) : null}
            {practitioner ? (
              <NavLink
                className='btn-quick-access btn-icon w-full'
                to='/admin/clients?create=true&redirectTo=dashboard'
              >
                {i18n.t('buttons.create.client')}
                <AddClientIcon className='nav-option-icon practitioner' />
              </NavLink>
            ) : null}
            <NavLink
              className='btn-quick-access btn-icon w-full'
              to='/admin/remote-view'
            >
              {i18n.t('buttons.remote-view')}
              <RemoteViewIcon className='nav-option-icon remote-view' />
            </NavLink>

            {userIsAdmin || canManageTeams ? (
              <NavLink
                className='btn-quick-access btn-icon w-full'
                to='/admin/practitioners?create=true'
              >
                {i18n.t('buttons.create.practitioner')}
                <AddClientIcon className='nav-option-icon practitioner' />
              </NavLink>
            ) : null}
            {AdminButtons}
          </Box>
          {allowedToSeeRecentSessions && (
            <Box className='data-table-wrapper row h-full'>
              <StyledEngineProvider injectFirst>
                <ThemeProvider
                  theme={getMuiTheme(
                    { cursor: 'pointer' },
                    {
                      '&:nth-of-type(2)': {
                        width: 120,
                      },
                      '&:nth-of-type(4)': {
                        width: 150,
                      },
                      '&:nth-of-type(5)': {
                        width: 150,
                      },
                      '&:nth-of-type(6)': {
                        width: 200,
                      },
                    },
                  )}
                >
                  <MUIDataTable
                    ref={tableRef}
                    data={
                      isLoading
                        ? muiTableLoadingSkeleton('body', 5)
                        : sessionsToShow
                    }
                    columns={
                      isLoading ? muiTableLoadingSkeleton('header') : attributes
                    }
                    options={tableConfig(
                      options,
                      isLoading,
                      isDataTableEmpty,
                      tableRef,
                    )}
                    className={classNames('mui-data-table dashboard', {
                      loading: isLoading,
                    })}
                  />
                </ThemeProvider>
              </StyledEngineProvider>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

function mapStateToProps(state) {
  return { ...state };
}

const mapDispatchToProps = {
  getAllStatistics: dashboardActions.getAllStatistics,
};

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(DashboardPage),
);
